/**
 * Sending every filled slot to the server, in slot order, then starting the run.
 *
 * <h2>Why one request per part, and one after another</h2>
 *
 * The server accepts 10 MB per request and the whole export is around 35 MB, so the
 * batch cannot travel as one upload. Each part is its own request carrying the shared
 * session id and its place in the sequence; the server queues it and runs nothing
 * until {@code start} is called. Sequential rather than parallel because the ORDER is
 * the point: the sequence number is assigned here, and a parallel upload that landed
 * out of order would still run in order — but a failed one would leave a gap the
 * person has to understand.
 *
 * <h2>🔑 Nothing starts if any upload fails</h2>
 *
 * A batch with its Customers file missing runs every other file against nobody. The
 * queued parts stay queued — visible in the log, harmless — and the person is told
 * which file did not make it.
 */

import { importMachanicdeskApi, startImportsApi } from '../../services/importApi';

/**
 * @param {Array<{kind: string, parts: Array<{file: File}>}>} filled the slots with a
 *        file, ALREADY in slot order
 * @param {{ sessionId?: string, onProgress?: (done: number, total: number) => void }} options
 * @returns {Promise<{ sessionId: string, uploaded: number, started: number }>}
 */
export async function uploadBatch(filled, { sessionId = newSessionId(), onProgress } = {}) {
    const total = filled.reduce((n, slot) => n + slot.parts.length, 0);
    let sequence = 0;
    for (const slot of filled) {
        for (const part of slot.parts) {
            sequence += 1;
            await importMachanicdeskApi(part.file, { sessionId, sequence });
            onProgress?.(sequence, total);
        }
    }
    const result = await startImportsApi(sessionId);
    return { sessionId, uploaded: sequence, started: result?.started ?? sequence };
}

/**
 * One id per press of Start, shared by every part of it.
 *
 * `crypto.randomUUID` where the browser has it — every supported one does — with a
 * fallback so a test runtime without it does not fail the upload for a reason that
 * has nothing to do with importing.
 */
export function newSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === 'x' ? r : (r % 4) + 8).toString(16);
    });
}
